/** SUPPORT_CONTACTS control: list the operator's support contacts and edit them
 *  (add / edit / remove) in a dialog. Reads current contacts + type choices from
 *  item.detail; saves the full set via the save_contacts endpoint.
 *
 *  Social links (backend plan 2026-10-29-operator-social-links §4): a SOCIAL row shows the
 *  network the backend derived from its URL and who wrote it (booking system / website /
 *  manual); "Find on website" re-scans the operator's site (find_social_links). Discovery only
 *  ever changes its own rows; editing a discovered link here makes it a manual one.
 */
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import {
  Box, Button, Checkbox, Chip, IconButton, MenuItem, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { PreflightItem } from '@/api/preflight';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { supportContactActions } from '../tenantLinks';

interface ContactRow {
  id?: number;
  contact_type: number;
  subtype?: number;
  name: string;
  contact: string;
  enabled: boolean;
  /** SOCIAL rows: derived by the backend from the URL (read-only). */
  network?: string;
  /** '' (from before the column) | manual | restech | website (read-only). */
  source?: string;
}

const SOCIAL = 4;
const NETWORK_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  x: 'X',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  tripadvisor: 'Tripadvisor',
  pinterest: 'Pinterest',
  linkedin: 'LinkedIn',
  yelp: 'Yelp',
  trustpilot: 'Trustpilot',
  google: 'Google',
  other: 'Other link',
};
const SOURCE_LABELS: Record<string, string> = {
  restech: 'from booking system',
  website: 'from website',
  manual: 'manual',
};

/** "Instagram · from website" for a SOCIAL row; null for the other types. */
function socialCaption(r: ContactRow): string | null {
  if (r.contact_type !== SOCIAL) return null;
  const parts = [NETWORK_LABELS[r.network ?? ''] ?? '', SOURCE_LABELS[r.source ?? ''] ?? ''].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}
interface TypeChoice {
  value: number;
  label: string;
}

function parseRows(detail: Record<string, unknown>): ContactRow[] {
  const raw = Array.isArray(detail.support_contacts) ? detail.support_contacts : [];
  return raw.map((r) => {
    const o = r as Record<string, unknown>;
    return {
      id: typeof o.id === 'number' ? o.id : undefined,
      contact_type: typeof o.contact_type === 'number' ? o.contact_type : 1,
      subtype: typeof o.subtype === 'number' ? o.subtype : 0,
      name: String(o.name ?? ''),
      contact: String(o.contact ?? ''),
      enabled: o.enabled !== false,
      network: typeof o.network === 'string' ? o.network : '',
      source: typeof o.source === 'string' ? o.source : '',
    };
  });
}

/** `detail.seen_ids`: the contact ids this read shows; undefined from an older backend. */
function parseSeenIds(detail: Record<string, unknown>): number[] | undefined {
  return Array.isArray(detail.seen_ids)
    ? detail.seen_ids.filter((v): v is number => typeof v === 'number')
    : undefined;
}

function parseTypes(detail: Record<string, unknown>): TypeChoice[] {
  const raw = Array.isArray(detail.contact_types) ? detail.contact_types : [];
  return raw.map((t) => {
    const o = t as Record<string, unknown>;
    return { value: Number(o.value), label: String(o.label ?? o.value) };
  });
}

export function SupportContactsControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<ContactRow[]>([]);
  // The ids the dialog was opened on: a contact written since (discovery, the dashboard) is not deleted.
  const [seenIds, setSeenIds] = useState<number[] | undefined>(undefined);
  const types = parseTypes(item.detail);
  const current = parseRows(item.detail);
  const typeLabel = (v: number) => types.find((t) => t.value === v)?.label ?? String(v);
  const website = typeof item.detail.website === 'string' ? item.detail.website : '';

  const save = useMutation({
    mutationFn: (payload: ContactRow[]) => items.saveContacts(topId, item.id, payload, seenIds),
    onSuccess: () => {
      snackbar.show('Support contacts saved.', 'success');
      setOpen(false);
      onChanged();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  const find = useMutation({
    mutationFn: () => items.findSocialLinks(topId, item.id),
    onSuccess: (res) => {
      snackbar.show(`Website scanned: ${res.summary}.`, 'success');
      onChanged();
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'The website could not be scanned.', 'error'),
  });

  const setRow = (i: number, patch: Partial<ContactRow>) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRow = () =>
    setRows((rs) => [...rs, { contact_type: types[0]?.value ?? 1, name: '', contact: '', enabled: true }]);
  const removeRow = (i: number) => setRows((rs) => rs.filter((_, j) => j !== i));

  const valid = rows.every((r) => r.name.trim() !== '' && r.contact.trim() !== '');

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Support contacts: {current.length || 'none'}
        </Typography>
        {canManage && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => {
              setRows(parseRows(item.detail));
              setSeenIds(parseSeenIds(item.detail));
              setOpen(true);
            }}
          >
            Edit contacts
          </Button>
        )}
        {canManage && (
          <Tooltip
            title={
              website
                ? `Read ${website} and add the social profiles it links to (links set by hand are never changed).`
                : 'The operator has no website URL.'
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<TravelExploreIcon />}
                disabled={!website || find.isPending}
                onClick={() => find.mutate()}
              >
                {find.isPending ? 'Scanning…' : 'Find on website'}
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>
      {current.length > 0 && (
        <Stack spacing={0.25}>
          {current.map((c) => (
            <Typography key={c.id} variant="body2" color={c.enabled ? 'text.primary' : 'text.disabled'}>
              {typeLabel(c.contact_type)}: {c.name} —{' '}
              {/* Only the scheme the value supports: an address mails, a number
                  dials, anything else is copyable and nothing more. */}
              <ValueActions actions={supportContactActions(c.contact)}>{c.contact}</ValueActions>
              {c.enabled ? '' : ' (disabled)'}
              {socialCaption(c) && (
                <Chip component="span" size="small" variant="outlined" label={socialCaption(c)} sx={{ ml: 1 }} />
              )}
            </Typography>
          ))}
        </Stack>
      )}

      <AppDialog
        open={open}
        onClose={() => setOpen(false)}
        title="Support contacts"
        size="md"
        actions={
          <>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" disabled={!valid || save.isPending} onClick={() => save.mutate(rows)}>
              Save
            </Button>
          </>
        }
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 150 }}>Type</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell sx={{ width: 72 }} align="center">
                Enabled
              </TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={i}>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    variant="standard"
                    value={r.contact_type}
                    onChange={(e) => setRow(i, { contact_type: Number(e.target.value) })}
                  >
                    {types.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    variant="standard"
                    value={r.name}
                    onChange={(e) => setRow(i, { name: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    variant="standard"
                    value={r.contact}
                    onChange={(e) => setRow(i, { contact: e.target.value })}
                    helperText={socialCaption(r) ?? undefined}
                  />
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    size="small"
                    checked={r.enabled}
                    onChange={(e) => setRow(i, { enabled: e.target.checked })}
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" aria-label="Remove" onClick={() => removeRow(i)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Button size="small" sx={{ mt: 1 }} onClick={addRow}>
          Add contact
        </Button>
        {!valid && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
            Every row needs a name and contact.
          </Typography>
        )}
      </AppDialog>
    </Box>
  );
}
